/**
 * @file useTimeFormat
 * Hook that provides time formatting utilities based on operator clock type settings.
 */

import { useMemo, useCallback } from "react";
import moment, { Moment } from "moment-timezone";
import { useOperator } from "./useOperator";
import { ClockTypeEnum } from "types";

export type TimeFormatOptions = {
  /** Include date in the output */
  includeDate?: boolean;
  /** Date format to use when includeDate is true */
  dateFormat?: string;
};

export type TimeFormats = {
  /** Time format string (e.g., "h:mm A" or "HH:mm") */
  time: string;
  /** Time format with seconds */
  timeWithSeconds: string;
  /** Format function for Moment objects */
  formatTime: (
    date: Moment | Date | string | null | undefined,
    options?: TimeFormatOptions
  ) => string;
  /** Get the appropriate format string */
  getTimeFormat: (options?: TimeFormatOptions) => string;
  /** Check if using 24-hour format */
  is24Hour: boolean;
};

export function useTimeFormat(): TimeFormats {
  const { operator } = useOperator();
  const clockType = operator?.settings?.clockType || ClockTypeEnum.TwelveHour;

  const formats = useMemo(() => {
    const is24Hour = clockType === ClockTypeEnum.TwentyFourHour;

    // Base format strings
    const timeFormat = is24Hour ? "HH:mm" : "h:mm A";
    const timeWithSecondsFormat = is24Hour ? "HH:mm:ss" : "h:mm:ss A";

    return {
      time: timeFormat,
      timeWithSeconds: timeWithSecondsFormat,
      is24Hour,
    };
  }, [clockType]);

  const formatTime = useCallback(
    (
      date: Moment | Date | string | null | undefined,
      options: TimeFormatOptions = {}
    ): string => {
      if (!date) {
        return "";
      }

      const momentDate = moment(date);
      const timeFormat = formats.time;

      let formatted: string;

      if (options.includeDate && options.dateFormat) {
        // When including date, append the time format to the date format
        const fullFormat = `${options.dateFormat} ${timeFormat}`;
        formatted = momentDate.format(fullFormat);
      } else {
        formatted = momentDate.format(timeFormat);
      }

      return formatted;
    },
    [formats]
  );

  const getTimeFormat = useCallback((): string => {
    return formats.time;
  }, [formats]);

  return {
    ...formats,
    formatTime,
    getTimeFormat,
  };
}
